import { useCallback, useEffect, useRef } from "react";

import { notifyError } from "common/utils/app-monitoring/notify";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { track } from "jiffy-analytics";
import { api, type models } from "jiffy-api";
import { shouldReportError } from "common/utils/request/request";
import {
  INITIAL_DELAY,
  POLLING_INTERVAL,
  MAX_POLLING_DURATION,
  normalizeArtworkResponse
} from "@design-lab/shared/lib/enhancementPolling";
import type {
  PollingState,
  NormalizedArtwork,
  RawArtworkResponse
} from "@design-lab/shared/lib/enhancementPolling";

type UseEnhancementPollingOptions = {
  artworkId: number;
  startTime: number;
  onEnhancementComplete: (artwork: NormalizedArtwork) => void | Promise<void>;
  onEnhancementFailed?: (errorMessage: string) => void;
  enabled?: boolean;
  isArtworkPresent?: boolean;
};

export function useEnhancementPolling({
  artworkId,
  startTime,
  onEnhancementComplete,
  onEnhancementFailed,
  enabled = true,
  isArtworkPresent = true
}: UseEnhancementPollingOptions) {
  const pollingState = useRef<PollingState>({
    isPolling: false,
    pollCount: 0
  });

  const stopPolling = useCallback(() => {
    if (pollingState.current.timeoutId) {
      clearTimeout(pollingState.current.timeoutId);
      pollingState.current.timeoutId = undefined;
    }
    pollingState.current.isPolling = false;
  }, []);

  const pollArtworkDetails = useCallback(async () => {
    if (!enabled || !pollingState.current.isPolling) {
      return;
    }

    if (pollingState.current.pollCount === 1 && artworkId) {
      track("fallback_polling_started", {
        artwork_id: String(artworkId),
        page: "design_lab"
      });
    }

    try {
      const rawResponse = await api.dtf.getArtworkHistory({
        artwork_ids: [artworkId],
        catalog_number: "DTF001"
      } as models.dtf.getArtworkHistory.Request);

      const response = objNamesToCamelCase(
        rawResponse
      ) as unknown as models.dtf.getArtworkHistoryDetails.Response;

      const artwork = response.artworks.find(
        (a: models.dtf.getArtworkHistoryDetails.DetailedArtwork) => Number(a.id) === artworkId
      );

      const normalizedArtwork = artwork
        ? normalizeArtworkResponse(artwork as RawArtworkResponse)
        : null;
      const enhancementState = normalizedArtwork?.currentEnhancementState;

      if (enhancementState === "complete_processing") {
        stopPolling();

        track("fallback_polling_enhancement_completed", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });

        if (normalizedArtwork) {
          onEnhancementComplete(normalizedArtwork);
        }
        return;
      }
    } catch (error) {
      const err = error as { status?: number; response?: { status?: number } };
      const status = err?.status || err?.response?.status;
      if (shouldReportError(error) && !(status && status >= 400 && status < 500)) {
        notifyError("Enhancement polling error", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            stack_trace: error instanceof Error ? error.stack : null,
            hook: "useEnhancementPolling",
            artworkId
          }
        });
      }
    }

    // Schedule next poll after processing
    if (!pollingState.current.isPolling) {
      return;
    }

    const elapsedTime = Date.now() - startTime;

    if (elapsedTime >= MAX_POLLING_DURATION) {
      stopPolling();

      if (artworkId) {
        const artworkDetails = await api.dtf.getArtwork(
          { id: artworkId },
          { catalog_number: "DTF001" }
        );
        if (artworkDetails.enhancement_status === "failed") {
          onEnhancementFailed?.("Something went wrong. Please try again.");
        }

        track("fallback_polling_stopped", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });
      }
      return;
    }

    const nextInterval = POLLING_INTERVAL;
    const remainingTime = MAX_POLLING_DURATION - elapsedTime;

    if (nextInterval > remainingTime) {
      return;
    }

    pollingState.current.timeoutId = setTimeout(() => {
      pollingState.current.pollCount += 1;
      pollArtworkDetails();
    }, nextInterval);
  }, [artworkId, startTime, onEnhancementComplete, onEnhancementFailed, enabled, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingState.current.isPolling || !enabled) {
      return;
    }

    pollingState.current.isPolling = true;
    pollingState.current.pollCount = 0;

    pollingState.current.timeoutId = setTimeout(() => {
      pollingState.current.pollCount += 1;
      pollArtworkDetails();
    }, INITIAL_DELAY);
  }, [enabled, pollArtworkDetails]);

  useEffect(() => {
    if (!isArtworkPresent && pollingState.current.isPolling) {
      stopPolling();

      if (artworkId) {
        track("fallback_polling_artwork_removed", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });
      }
    }
  }, [isArtworkPresent, stopPolling, artworkId]);

  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling]
  );

  return {
    startPolling,
    stopPolling,
    isPolling: pollingState.current.isPolling
  };
}
